import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Send, Volume2, VolumeX, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIPS = [
  "Who do you represent?",
  "How do I get signed?",
  "How do brands partner?",
];

function PickleballIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <circle cx="12" cy="12" r="10" fill="#84CC16" />
      <circle cx="8.5" cy="8.5" r="1.5" fill="#07090E" />
      <circle cx="14.5" cy="7.5" r="1.5" fill="#07090E" />
      <circle cx="12" cy="12.5" r="1.5" fill="#07090E" />
      <circle cx="17" cy="12.5" r="1.5" fill="#07090E" />
      <circle cx="6.5" cy="13.5" r="1.5" fill="#07090E" />
    </svg>
  );
}

function speak(text) {
  if (!("speechSynthesis" in window) || !text) return;
  window.speechSynthesis.cancel();
  const clean = text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    .replace(/[*_#`>]/g, "");
  const utterance = new SpeechSynthesisUtterance(clean);
  utterance.lang = "en-US";
  utterance.rate = 1;
  window.speechSynthesis.speak(utterance);
}

function Bubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${isUser ? "user" : "assistant"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
          isUser
            ? "rounded-br-sm bg-volt text-ink"
            : "rounded-bl-sm border border-white/10 bg-ink/70 text-slate-200"
        }`}
      >
        {msg.content || <span className="inline-flex gap-1 py-1">{[0, 1, 2].map((i) => (
          <span key={i} className="h-1.5 w-1.5 animate-bounce rounded-full bg-volt" style={{ animationDelay: `${i * 0.15}s` }} />
        ))}</span>}
      </div>
    </div>
  );
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  useEffect(() => {
    return () => window.speechSynthesis?.cancel();
  }, []);

  const send = async (text) => {
    const clean = (text || "").trim();
    if (!clean || busy) return;
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    const history = messages.slice(-8).map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { role: "user", content: clean }, { role: "assistant", content: "" }]);
    setInput("");
    setBusy(true);
    let full = "";
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: clean, history }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          try {
            const { delta } = JSON.parse(payload);
            if (delta) {
              full += delta;
              setMessages((m) => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                copy[copy.length - 1] = { role: "assistant", content: (last?.content || "") + delta };
                return copy;
              });
            }
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry — I hit a snag. Please try again, or email brian@smgmediagroup.com.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
      if (voiceOn && full) speak(full);
    }
  };

  return (
    <>
      <button
        onClick={() => {
          if (open && "speechSynthesis" in window) window.speechSynthesis.cancel();
          setOpen(!open);
        }}
        data-testid="assistant-open-btn"
        aria-label={open ? "Close assistant" : "Open assistant"}
        className="fixed bottom-5 right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-volt shadow-[0_8px_32px_rgba(132,204,22,0.4)] transition-transform hover:scale-105"
      >
        {open ? (
          <X className="h-6 w-6 text-ink" />
        ) : (
          <PickleballIcon className="h-8 w-8" />
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 z-[70] flex h-[520px] max-h-[70vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-3xl border border-white/10 bg-elev shadow-[0_24px_80px_rgba(0,0,0,0.6)]"
            data-testid="assistant-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/10 bg-surface px-5 py-4">
              <PickleballIcon className="h-7 w-7" />
              <div>
                <p className="font-display text-sm uppercase tracking-wider text-white">SMG Assistant</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-slate-500">AI · verify details with the team</p>
              </div>
              <button
                onClick={() => {
                  if (voiceOn && "speechSynthesis" in window) window.speechSynthesis.cancel();
                  setVoiceOn(!voiceOn);
                }}
                data-testid="assistant-voice-toggle"
                aria-label={voiceOn ? "Voice replies on — tap to mute" : "Voice replies off — tap to have answers read aloud"}
                aria-pressed={voiceOn}
                title={voiceOn ? "Voice on — tap to mute" : "Voice off — tap to hear replies"}
                className={`ml-auto flex h-9 w-9 shrink-0 items-center justify-center rounded-full border transition-colors ${
                  voiceOn
                    ? "border-volt bg-volt"
                    : "border-white/15 bg-ink/60 hover:border-volt/50"
                }`}
              >
                {voiceOn ? (
                  <Volume2 className="h-4 w-4 text-ink" />
                ) : (
                  <VolumeX className="h-4 w-4 text-volt" />
                )}
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
              {messages.length === 0 && (
                <div className="pt-4">
                  <p className="text-center text-sm leading-relaxed text-slate-400">
                    Hey! Ask me anything about SMG — our athletes, services or how to get signed.
                  </p>
                  <div className="mt-5 space-y-2">
                    {CHIPS.map((c) => (
                      <button
                        key={c}
                        onClick={() => send(c)}
                        data-testid={`assistant-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className="w-full rounded-xl border border-white/10 bg-surface px-4 py-3 text-left text-xs text-slate-300 transition-colors hover:border-volt/50 hover:text-volt"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <Bubble key={i} msg={m} />
              ))}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send(input);
              }}
              className="flex items-center gap-2 border-t border-white/10 bg-surface px-3 py-3"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about SMG…"
                data-testid="assistant-input"
                className="flex-1 rounded-full border border-white/10 bg-ink/60 px-4 py-2.5 text-base text-white placeholder:text-slate-500 outline-none transition-all focus:border-volt/70 focus:ring-2 focus:ring-volt/20"
              />
              <button
                type="submit"
                disabled={busy || !input.trim()}
                data-testid="assistant-send-btn"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-volt text-ink transition-all hover:bg-volt-light disabled:opacity-40"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
